import { GetListParams, GetOneParams, Identifier, RaRecord } from 'react-admin';
import { CustomDataProvider, registerModuleDataProvider } from '../../providers/CustomDataProvider';
import { baseUrl, httpClient } from '../../providers/AppDataProvider';

const resourceBase = 'prediction/admin-builder-trade-stats';

export const predictionDashboardResources = {
  userGrowth: 'prediction-dashboard/user-growth',
  userGrowthByDay: 'prediction-dashboard/user-growth-by-day',
  totals: 'prediction-dashboard/totals',
  byDay: 'prediction-dashboard/by-day',
  activeWallets: 'prediction-dashboard/active-wallets',
  revenue: 'prediction-dashboard/revenue',
} as const;

export type PredictionDashboardFilter = {
  fromTime?: string | number;
  toTime?: string | number;
  tradingType?: 'Perp' | 'Meme' | 'Predict' | 'TapGrid';
  depositType?: string;
  withdrawType?: string;
};

export type PredictionByDayItem = RaRecord & {
  date: string;
  notional: string;
  tradeCount: number;
};

export type PredictionUserGrowthItem = RaRecord & {
  date: string;
  totalNewUsers?: string | number;
  newUsers?: string | number;
  users?: string | number;
};

export type PredictionUserGrowthSummary = RaRecord & {
  totalNewUsers?: string | number;
  newUsers?: string | number;
  users?: string | number;
};

export type PredictionTotalsSummary = RaRecord & {
  usdc?: string | number;
  notional?: string | number;
};

export type PredictionActiveWalletsSummary = RaRecord & {
  total?: string | number;
};

type PredictionByDayRow = {
  id?: Identifier;
  date: string;
  notional: string;
  tradeCount: number;
};

type PredictionUserGrowthRow = {
  id?: Identifier;
  date: string;
  totalNewUsers?: string | number;
  newUsers?: string | number;
  users?: string | number;
};

type PredictionChartRow = PredictionByDayRow | PredictionUserGrowthRow;
type PredictionSummaryRow = PredictionUserGrowthSummary | PredictionTotalsSummary | PredictionActiveWalletsSummary;
type PredictionSummaryPayload = Partial<Omit<PredictionSummaryRow, 'id'>> & Partial<Pick<PredictionSummaryRow, 'id'>>;

type PredictionDashboardResource =
  (typeof predictionDashboardResources)[keyof typeof predictionDashboardResources];

const endpointMap: Record<PredictionDashboardResource, string> = {
  [predictionDashboardResources.userGrowth]: 'user-growth',
  [predictionDashboardResources.userGrowthByDay]: 'user-growth/by-day',
  [predictionDashboardResources.totals]: 'totals',
  [predictionDashboardResources.byDay]: 'by-day',
  [predictionDashboardResources.activeWallets]: 'active-wallets',
  [predictionDashboardResources.revenue]: 'builder-fee-by-day',
};

const chartResources = new Set<PredictionDashboardResource>([
  predictionDashboardResources.userGrowthByDay,
  predictionDashboardResources.byDay,
]);

const summaryResources = new Set<PredictionDashboardResource>([
  predictionDashboardResources.userGrowth,
  predictionDashboardResources.totals,
  predictionDashboardResources.activeWallets,
]);

const toIsoString = (value?: string | number | boolean | string[]) => {
  if (typeof value === 'number') return new Date(value).toISOString();
  if (typeof value === 'string') {
    const timestamp = Number(value);
    if (!Number.isNaN(timestamp) && value.trim() !== '') {
      return new Date(timestamp).toISOString();
    }

    return value;
  }

  return undefined;
};

const buildDateRangeParams = (filter: PredictionDashboardFilter = {}) => ({
  from: toIsoString(filter.fromTime),
  to: toIsoString(filter.toTime),
});

const buildQueryParams = (filter: PredictionDashboardFilter = {}) => {
  const queryParams = new URLSearchParams();
  const dateRangeParams = buildDateRangeParams(filter);

  Object.entries(dateRangeParams).forEach(([key, value]) => {
    if (value) queryParams.set(key, value);
  });

  return queryParams;
};

const buildSummaryRecord = (
  payload: PredictionSummaryPayload,
  id: Identifier = 'summary'
): PredictionSummaryRow => ({
  ...payload,
  id: payload.id ?? id,
} as PredictionSummaryRow);

const buildChartRecord = <TRow extends PredictionChartRow>(
  row: TRow,
  fallbackId: Identifier
): TRow & RaRecord => ({
  ...row,
  id: row.id ?? fallbackId,
});

class PredictionDashboardDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    if (!(resource in endpointMap)) {
      return Promise.reject(new Error('Unsupported resource'));
    }

    const typedResource = resource as PredictionDashboardResource;
    const queryParams = buildQueryParams(params.filter ?? {});

    const endpoint = endpointMap[typedResource];
    const url = `${baseUrl}/${resourceBase}/${endpoint}?${queryParams.toString()}`;
    const { json } = await httpClient(url, { method: 'GET' });

    if (chartResources.has(typedResource)) {
      const rows: PredictionChartRow[] = json?.items ?? json?.data ?? json?.list ?? json?.records ?? [];
      return {
        data: rows.map((row, index) => buildChartRecord(row, row.date ?? `${typedResource}-${index}`)),
        total: rows.length,
      };
    }

    const payload: PredictionSummaryPayload = json?.data ?? json ?? {};
    return {
      data: [buildSummaryRecord(payload)],
      total: 1,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async getOne(resource: string, params: GetOneParams): Promise<any> {
    if (!(resource in endpointMap)) {
      return Promise.reject(new Error('Unsupported resource'));
    }

    const typedResource = resource as PredictionDashboardResource;
    if (!summaryResources.has(typedResource)) {
      return Promise.reject(new Error('getOne is only supported for summary resources'));
    }

    const filter = (params.meta ?? {}) as PredictionDashboardFilter;
    const queryParams = buildQueryParams(filter);
    const endpoint = endpointMap[typedResource];
    const url = `${baseUrl}/${resourceBase}/${endpoint}?${queryParams.toString()}`;
    const { json } = await httpClient(url, { method: 'GET' });
    const payload: PredictionSummaryPayload = json?.data ?? json ?? {};

    return {
      data: buildSummaryRecord(payload, params.id),
    };
  }
}

const predictionDashboardDataProvider = new PredictionDashboardDataProvider();

Object.values(predictionDashboardResources).forEach((resource) => {
  registerModuleDataProvider(resource, predictionDashboardDataProvider);
});
